import React, { useState } from 'react';
import { ArrowUpRight, Check } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const SUPPORT_EMAIL = 'abdulbasit@thedressup.co';
const ERROR_TEXT = '#ff7a6e';

const DETAILS = [
  {
    label: 'Email',
    value: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
    note: 'General questions and support.',
  },
  {
    label: 'Phone',
    value: '+92 300 123 4567',
    href: 'tel:+923001234567',
    note: 'Monday to Friday, 9am to 5pm PKT.',
  },
] as const;

const ContactUsPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [handedOff, setHandedOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // There is no submit endpoint for this form, so rather than pretend a message
  // was sent, hand the composed message to the visitor's own mail client.
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in every field before sending.');
      return;
    }

    const subject = `Message from ${name.trim()}`;
    const body = `${message.trim()}\n\nFrom: ${name.trim()}\nReply to: ${email.trim()}`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    setHandedOff(true);
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <Navigation />

      <main className="flex-grow pt-28 md:pt-32">
        <div className="shell">
          <div className="rule pt-12 lg:pt-16">
            <h1 className="display max-w-[14ch] text-[clamp(2.5rem,6vw,4.5rem)] font-medium text-ink">
              Talk to us.
            </h1>
            <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-muted md:text-lg">
              Questions about your palette, a store listing, or your account. We read everything
              that comes in.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-12 pb-20 lg:grid-cols-12 lg:gap-16 lg:pb-28">
            {/* Direct routes first: most people want the address, not a form. */}
            <div className="lg:col-span-5">
              {DETAILS.map((detail) => (
                <div key={detail.label} className="border-t border-line py-7">
                  <p className="text-[13px] font-semibold text-ink">{detail.label}</p>
                  <a
                    href={detail.href}
                    className="display mt-3 block break-words text-[clamp(1.25rem,2.2vw,1.75rem)] font-medium text-ink transition-colors hover:text-accent"
                  >
                    {detail.value}
                  </a>
                  <p className="mt-2 text-[14px] text-muted">{detail.note}</p>
                </div>
              ))}

              <div className="border-y border-line py-7">
                <p className="text-[13px] font-semibold text-ink">Office</p>
                <p className="mt-3 max-w-[32ch] text-[15px] leading-relaxed text-muted">
                  NITB Building, National Incubation Center, 24-B Street 6, Sector H-9/1 H-9,
                  Islamabad 44000, Pakistan
                </p>
              </div>
            </div>

            {/* Composer panel, same language as the signup form. */}
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="panel-glass px-5 py-8 sm:px-6 sm:py-10 md:px-10 md:py-12">
                {handedOff ? (
                  <div>
                    <span
                      className="flex h-11 w-11 items-center justify-center"
                      style={{ backgroundColor: '#7fd6a8' }}
                    >
                      <Check strokeWidth={2} className="h-6 w-6" style={{ color: '#111113' }} />
                    </span>

                    <h2 className="display mt-6 text-3xl font-medium">Your draft is ready.</h2>

                    <p className="mt-4 max-w-[40ch] text-[15px] leading-relaxed text-panel-muted">
                      We opened your mail app with the message filled in. Send it from there and it
                      reaches us directly.
                    </p>

                    <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-on-panel group mt-8">
                      Open mail app again
                      <ArrowUpRight
                        strokeWidth={1.5}
                        className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </a>

                    <button
                      type="button"
                      onClick={() => setHandedOff(false)}
                      className="mt-6 block text-[14px] text-panel-muted underline underline-offset-4 transition-colors hover:text-panel-ink"
                    >
                      Write another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate>
                    <h2 className="display text-3xl font-medium">Send a message</h2>

                    <div className="mt-8 grid grid-cols-1 gap-7">
                      <div>
                        <label htmlFor="contact-name" className="field-label text-panel-muted">
                          Name
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          required
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            setError(null);
                          }}
                          className="field-input mt-2"
                          placeholder="Amara Osei"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="field-label text-panel-muted">
                          Email
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setError(null);
                          }}
                          className="field-input mt-2"
                          placeholder="you@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="field-label text-panel-muted">
                          Message
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={5}
                          required
                          value={message}
                          onChange={(e) => {
                            setMessage(e.target.value);
                            setError(null);
                          }}
                          className="field-input mt-2 resize-none"
                          placeholder="What can we help with?"
                        />
                      </div>
                    </div>

                    <button type="submit" className="btn btn-on-panel group mt-9 w-full sm:w-auto">
                      Compose in my mail app
                      <ArrowUpRight
                        strokeWidth={1.5}
                        className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </button>

                    <p className="mt-4 text-[13px] text-panel-muted">
                      This opens your own mail app with the message ready to send.
                    </p>

                    {error && (
                      <p role="alert" className="mt-5 text-[13px]" style={{ color: ERROR_TEXT }}>
                        {error}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
